import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { CaseWorkflow } from '@app/shared/models/workflow';
import { HttpErrorResponse } from '@angular/common/http';
import {
    CategoryTemplateList,
    DistributionJob,
    DistributionJobChannels,
    DistributionJobState, TemplateCategory
} from '@app/shared/models/distribution-doc-models';
import { DistributionService, WorkflowDistributionJobUriBuilderParams } from '@app/core/services/distribution.service';
import { DistributionJobListComponent } from '@app/shared/components/distribution-job-list/distribution-job-list.component';
import { TranslationEntry } from '@app/shared/models/translatable';
import { BaseWithIdList } from '@app/shared/models/list';
import { Receiver } from '@app/shared/models/participant';
import { ParticipantsService } from '@app/core/services/participants.service';
import { Module } from '@app/core/authentication/permission';
import { TemplateService } from '@app/core/services/template.service';
import { DrFileMeta, DrFileMetaCategoryList } from '@app/shared/models/dr-file';
import { forkJoin } from 'rxjs';
import * as _ from 'lodash-es';


@Component({
    selector: 'case-workflow-distribution',
    templateUrl: './case-workflow-distribution.component.html',
    styleUrls: ['./case-workflow-distribution.component.scss']
})
export class CaseWorkflowDistributionComponent extends BaseWithBundleComponent
                                               implements OnInit {

    private _workflow: CaseWorkflow;

    @ViewChild('distributionJobListComponent')
    public distributionJobListComponent: DistributionJobListComponent;

    @Input()
    set workflow(workflow: CaseWorkflow) {
        if (workflow) {
            const changed = this._workflow && this._workflow.id !== workflow.id;
            this._workflow = workflow;
            this._workflow.defaults.distribution_jobs.items.forEach(job => {
                // Workflow jobs do not have a state,
                // but in order to make them editable in child components we set them to INITIAL.
                job.state = DistributionJobState.INITIAL;
            });

            if (changed) {
                this.ngOnInit();
            }
        }
    }

    get workflow(): CaseWorkflow {
        return this._workflow;
    }

    public unfilteredAllAssignables: DrFileMetaCategoryList;
    public receiverList: BaseWithIdList<Receiver>;
    public distributionChannels: TranslationEntry[];

    public constructor(protected componentServiceBundle: ComponentServiceBundle,
                       public distributionService: DistributionService,
                       public participantService: ParticipantsService,
                       public templateService: TemplateService
    ) {
        super(componentServiceBundle);
        this._idPrefix = 'distribution.';
    }

    public ngOnInit() {
        super.ngOnInit();
        this.receiverList = new BaseWithIdList(Receiver);
        this.receiverList.items = this.workflow.defaults.participants.items.map(part => {
            part.actionListDisplayName = this.participantService.getTranslatedParticipantTypes(part);
            return new Receiver(part)
        });

        this.distributionChannels = [
            DistributionJobChannels.PRINT,
            DistributionJobChannels.EMAIL,
            DistributionJobChannels.CLAIM_NET
        ];

        if (this.currentUser.hasModules(Module.MODULE_SIGNING)) {
            this.distributionChannels.push(DistributionJobChannels.SIGNING);
        }

        const allRequests = [
            this.templateService.getTemplateCategoryList([], false, true),
            this.templateService.getSMPDocumentTypes()
        ];

        forkJoin(allRequests).subscribe(allResults => {
            const templates = (<[]>allResults[0]).map((categoryTemplateList: CategoryTemplateList) => {
                const entries = categoryTemplateList.items.map(template => {
                    const fileMeta = new DrFileMeta();
                    fileMeta.document_key = template.key;
                    fileMeta.filename = template.display_name;
                    fileMeta.document_type = template.document_type;
                    fileMeta.is_template = true;
                    fileMeta.properties = template.properties;
                    return fileMeta;
                });

                return {
                    document_type: categoryTemplateList.template_category.key,
                    entries: entries
                };
            });

            let placeholders = (<TranslationEntry[]>allResults[1]).map((entry: TranslationEntry)=> {
                 const fileMeta = new DrFileMeta();
                fileMeta.filename = entry.trans;
                fileMeta.document_type = entry.key;
                fileMeta.is_placeholder = true;
                return fileMeta;
            });

            placeholders = _.sortBy(placeholders, placeholder => {
                return this.translateService.instant(placeholder.filename);
            });

            templates.push({
                document_type: TemplateCategory.SMP_PLACEHOLDER,
                entries: placeholders
            });

            this.unfilteredAllAssignables = new DrFileMetaCategoryList(templates);
        });

        this.workflow.defaults.distribution_jobs.items.forEach(job => {
            if (job.receiver && job.receiver.id) {
                const part = this.receiverList.getItem(job.receiver.id);

                if (!job.receiver.type.length) {
                    job.receiver.type = part.type.slice();
                }

                job.receiver.actionListDisplayName = this.participantService.getTranslatedParticipantTypes(part);
            }
        });
    }

    public create(): void {
        const newJob = new DistributionJob({
            workflow_id: this.workflow.id
        });

        this.distributionService.create(this.generateWorkflowDistributionJobUriBuilderParams(this.workflow), newJob)
                                .subscribe((result: DistributionJob) => {
            // Workflow jobs do not have a state,
            // but in order to make them editable in child components we set them to INITIAL.
            result.state = DistributionJobState.INITIAL;
            this.workflow.defaults.distribution_jobs.addItem(result, true);

            setTimeout(() => {
                this.distributionJobListComponent.selectDistributionJob(result, true);
            }, 0);
        }, err => {
            this.handleError(err);
        });
    }

    public detectChanges(): void {

    }

    public generateWorkflowDistributionJobUriBuilderParamsForJob(job: DistributionJob): WorkflowDistributionJobUriBuilderParams {
        return new WorkflowDistributionJobUriBuilderParams(job.workflow_id, job);
    }

    public generateWorkflowDistributionJobUriBuilderParams(workflow: CaseWorkflow | string, job?: DistributionJob): WorkflowDistributionJobUriBuilderParams {
        return new WorkflowDistributionJobUriBuilderParams(workflow, job);
    }

    protected handleError(error: HttpErrorResponse | Error | string): void {
        this.showSimpleErrorSnackbar();
        super.handleError(error);
    }

}
